import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import API from '../utils/api';
import { CheckCircle2, XCircle, Eye, RefreshCw, Users } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from './ui/dialog';

/**
 * Admin / edit-level Roster tab.
 *
 * Shows per-employee upload status for a given date (default = today).
 * Columns: Employee · Department · Uploaded · Hours · Uploaded On · View
 */
export default function TimesheetRosterTab({ board }) {
  const today = new Date().toISOString().split('T')[0];
  const [date, setDate] = useState(today);
  const [roster, setRoster] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState(null);

  const fetchRoster = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/daily-timesheet/roster`, {
        params: { board, date },
        withCredentials: true,
      });
      setRoster(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load roster');
    } finally { setLoading(false); }
  }, [board, date]);

  useEffect(() => { fetchRoster(); }, [fetchRoster]);

  const rows = roster?.rows || [];
  const uploaded = roster?.uploaded_count || 0;
  const total = roster?.total || 0;
  const pct = total > 0 ? Math.round((uploaded / total) * 100) : 0;

  return (
    <div className="animate-fadeIn space-y-4" data-testid="timesheet-roster-tab">
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500">Date</label>
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            className="bg-black/40 border border-white/10 text-white rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
            data-testid="roster-date-picker"
          />
        </div>
        <button
          onClick={fetchRoster}
          className="flex items-center gap-1 text-xs text-slate-400 hover:text-white px-2 py-1.5"
          data-testid="roster-refresh"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
        <div className="flex-1" />
        <div className="flex items-center gap-4 px-3 py-1.5 rounded-md bg-white/[0.03] border border-white/5">
          <div className="flex items-center gap-1.5 text-xs">
            <Users size={12} className="text-slate-400" />
            <span className="text-white font-semibold">{uploaded}</span>
            <span className="text-slate-500">/ {total} uploaded</span>
          </div>
          <div className="h-3 w-24 bg-white/5 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-[10px] text-slate-400 font-mono">{pct}%</span>
        </div>
      </div>

      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table text-xs">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Department</th>
                <th>Status</th>
                <th className="text-right">Hours</th>
                <th>Uploaded On</th>
                <th>View</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-12 text-slate-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-12 text-slate-500">No employees found for this board.</td></tr>
              ) : rows.map((r, i) => (
                <tr key={r.employee_id} data-testid={`roster-row-${r.employee_id}`}>
                  <td className="text-white font-medium">{r.name}</td>
                  <td className="text-slate-400">{r.department}</td>
                  <td>
                    {r.uploaded ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 text-[10px] font-semibold">
                        <CheckCircle2 size={10} /> UPLOADED
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-rose-500/10 text-rose-300 border border-rose-500/20 text-[10px] font-semibold">
                        <XCircle size={10} /> MISSING
                      </span>
                    )}
                  </td>
                  <td className="text-right text-slate-300 font-mono">
                    {(() => {
                      const h = r.total_hours ?? r.hours;
                      return h != null ? h.toFixed(2) : '—';
                    })()}
                    {r.tasks?.length > 1 && (
                      <span className="ml-1 text-[9px] text-slate-500">({r.tasks.length} tasks)</span>
                    )}
                  </td>
                  <td className="text-[10px] text-slate-500 font-mono">
                    {r.uploaded_at ? new Date(r.uploaded_at).toLocaleString('en-IN', { hour12: true }) : '—'}
                  </td>
                  <td>
                    {r.uploaded && (
                      <button
                        onClick={() => setViewing(r)}
                        className="flex items-center gap-1 text-[11px] text-indigo-400 hover:text-indigo-300"
                        data-testid={`roster-view-${r.employee_id}`}
                      >
                        <Eye size={11} /> View
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {viewing && (
        <Dialog open={true} onOpenChange={() => setViewing(null)}>
          <DialogContent className="max-w-lg bg-[#0a0a0f] border-white/10 max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white font-heading text-lg">{viewing.name}'s Timesheet</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="bg-white/[0.03] border border-white/5 rounded p-2">
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-0.5">Date</div>
                  <div className="text-white font-mono">{date}</div>
                </div>
                <div className="bg-emerald-500/5 border border-emerald-500/15 rounded p-2">
                  <div className="text-[9px] uppercase tracking-wider text-emerald-400 mb-0.5">Total Hours</div>
                  <div className="text-emerald-300 font-mono font-bold">{(viewing.total_hours ?? viewing.hours)?.toFixed(2)}h</div>
                </div>
                <div className="bg-indigo-500/5 border border-indigo-500/15 rounded p-2">
                  <div className="text-[9px] uppercase tracking-wider text-indigo-400 mb-0.5">Tasks</div>
                  <div className="text-indigo-300 font-mono font-bold">{viewing.tasks?.length || 1}</div>
                </div>
              </div>

              {viewing.tasks?.length > 0 ? (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 font-semibold">Task Breakdown</div>
                  <div className="space-y-2">
                    {viewing.tasks.map((t, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-3 bg-white/[0.02] border border-white/5 rounded p-2.5"
                        data-testid={`view-task-row-${i}`}
                      >
                        <div className="h-6 w-6 rounded bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center text-indigo-300 font-bold text-[10px] flex-shrink-0">
                          {i + 1}
                        </div>
                        <div className="flex-1 min-w-0 text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">
                          {t.description}
                        </div>
                        <div className="text-xs font-mono font-bold text-emerald-300 flex-shrink-0 ml-2">
                          {Number(t.hours).toFixed(2)}h
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : viewing.description && (
                <div>
                  <div className="text-xs text-slate-500 mb-1">Description</div>
                  <div className="text-sm text-slate-200 bg-white/[0.02] border border-white/5 rounded p-3 whitespace-pre-wrap leading-relaxed">
                    {viewing.description}
                  </div>
                </div>
              )}

              <div className="text-[10px] text-slate-500 font-mono pt-1">
                Submitted: {new Date(viewing.uploaded_at).toLocaleString('en-IN', { hour12: true })}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
